import type { ReactElement } from "react";
import {
  CloudIcon,
  FolderGit2Icon,
  FolderGitIcon,
  FolderIcon,
  GitBranchIcon,
  MonitorIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { STRIP_CONTROL_CLASS } from "./composerControlsStripChrome";
import type { ComposerControlsStripTrigger } from "./ComposerControlsStrip.logic";

/**
 * t3code's glyphs, one for one. Its `BranchToolbar` picks a machine icon for a
 * device and a cloud for a remote Environment. For the workspace it picks
 * `FolderGit2Icon` for a worktree the thread has asked for and does not have
 * yet, `FolderGitIcon` for one that exists, and `FolderIcon` for the checkout
 * itself, so the icon carries the same three-way distinction as the label
 * rather than repeating it generically.
 *
 * Forge has no `EnvironmentMachineKind`, so t3code's per-machine icon set maps
 * down to the one distinction Forge can actually make: this device or a cloud
 * Environment. Inventing the kind would mean a contracts change this card has
 * no business making.
 */
const TRIGGER_ICON = {
  cloud: CloudIcon,
  device: MonitorIcon,
  worktree: FolderGitIcon,
  pendingWorktree: FolderGit2Icon,
  checkout: FolderIcon,
  branch: GitBranchIcon,
} as const satisfies Record<ComposerControlsStripTrigger["kind"], typeof CloudIcon>;

/**
 * One settled fact, in the chrome of the picker it replaces.
 *
 * A real `<button disabled>` rather than a styled span: `disabled` is what
 * actually refuses the click, and pairing it with `aria-disabled` states the
 * same thing to a screen reader. No chevron, because a chevron on a fact you
 * cannot change is a lie about what the control does.
 */
export function LockedTrigger({
  trigger,
}: {
  readonly trigger: ComposerControlsStripTrigger;
}): ReactElement {
  const Icon = TRIGGER_ICON[trigger.kind];
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      aria-label={trigger.ariaLabel}
      data-chat-composer-context-chip={trigger.id}
      className={cn(
        STRIP_CONTROL_CLASS,
        "inline-flex min-w-0 shrink cursor-default items-center whitespace-nowrap border border-transparent text-base disabled:pointer-events-none md:shrink-0 md:text-xs",
      )}
    >
      <Icon aria-hidden="true" className="size-3 shrink-0" />
      <span className="min-w-0 max-w-36 truncate lg:max-w-[200px]">{trigger.label}</span>
    </button>
  );
}
